import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { authService } from '@/services/api.service';
import { phoneSchema, otpSchema, OtpFormValues, PhoneFormValues } from '@/lib/schemas';
import { formatTime, maskPhoneNumber } from '@/lib/utils';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { toast } from 'sonner';
import { Car, Shield } from 'lucide-react';

export default function AuthScreen() {
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [currentPhone, setCurrentPhone] = useState<string>('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Pure token validation function
  const validateToken = (token: string): { isValid: boolean; decodedData?: JwtPayload; error?: string } => {
    try {
      const decodedData = jwtDecode(token);
      const expirationTime = decodedData.exp;
      if (expirationTime && expirationTime > Date.now() / 1000) {
        return { isValid: true, decodedData };
      }
      return { isValid: false, error: 'Token expired' };
    } catch (error) {
      return { isValid: false, error: 'Invalid token format' };
    }
  };

  // Handle auth state
  const handleAuthState = (token: string) => {
    const validationResult = validateToken(token);
    
    if (validationResult.isValid && validationResult.decodedData) {
      localStorage.setItem('timelyyy_website_auth_token', token);
      navigate('/booking', { state: { customerDecodedData: validationResult.decodedData.sub } });
      return true;
    } else {
      localStorage.removeItem('timelyyy_website_auth_token');
      toast.error(
        'Authentication Error: ' + (validationResult.error === 'Token expired' 
          ? 'Your session has expired. Please login again.'
          : 'Invalid session. Please login again.'),
      );
      return false;
    }
  };

  // Check for token in query params and localStorage
  useEffect(() => {
    const validateAuth = () => {
      // First check query params
      const queryToken = searchParams.get('token');
      if (queryToken) {
        if (handleAuthState(queryToken)) {
          return; // Exit if query token is valid
        }
      }

      // If no query token or invalid, check localStorage
      const storedToken = localStorage.getItem('timelyyy_website_auth_token');
      if (storedToken) {
        handleAuthState(storedToken);
      }
    };

    validateAuth();

    // Run validation when the component mounts and when it gains focus
    window.addEventListener('focus', validateAuth);
    
    return () => {
      window.removeEventListener('focus', validateAuth);
    };
  }, [searchParams]);

  // Phone form
  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: '',
    },
  });

  // OTP form
  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      pin: '',
    },
  });

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  // Send OTP mutation
  const sendOtpMutation = useMutation({
    mutationFn: (phone: string) => authService.sendOtp(phone),
    onSuccess: (_data, phone) => {
      setResendTimer(90);
      setShowOtpForm(true);
      setMaskedPhone(maskPhoneNumber(phone));
      setCurrentPhone(phone);
      // Reset OTP form to ensure clean state
      setTimeout(() => {
        otpForm.reset({ pin: '' });
      }, 0);
    },
    onError: (error: Error) => {
        toast.error(
        'Error: ' + (error.message || 'Failed to send OTP. Please try again.'),
      );
    },
  });

  // Verify OTP mutation
  const verifyOtpMutation = useMutation({
    mutationFn: async (data: { phone: string; pin: string }) => {
      return authService.verifyOtp(data.phone, data.pin);
    },
    onSuccess: (data) => {
      if (data?.data?.token) {
        const validationResult = validateToken(data.data.token);
        
        if (validationResult.isValid && validationResult.decodedData) {
          localStorage.setItem('timelyyy_website_auth_token', data.data.token);
          toast.success(
            'Phone number verified successfully.'
          );
          navigate('/booking', { 
            state: { customerDecodedData: validationResult.decodedData.sub } 
          });
        } else {
          toast.error(
            'Authentication Error: ' + (validationResult.error === 'Token expired' 
              ? 'Your session has expired. Please try again.'
              : 'Invalid session. Please try again.'),
          );
          setShowOtpForm(false);
          setCurrentPhone('');
          setMaskedPhone('');
          otpForm.reset();
          phoneForm.reset();
        }
      } else {
        toast.error(
          'Authentication Error: No token received. Please try again.',
        );
        setShowOtpForm(false);
        setCurrentPhone('');
        setMaskedPhone('');
        otpForm.reset();
        phoneForm.reset();
      }
    },
    onError: (error: Error) => {
      toast.error(
        'Error: ' + (error.message || 'Invalid OTP. Please try again.'),
      );
      otpForm.reset();
      phoneForm.reset();
    },
  });

  const onPhoneSubmit = async (data: PhoneFormValues) => {
    sendOtpMutation.mutate(data.phone);
  };

  const onOtpSubmit = async (data: OtpFormValues) => {
    if (!currentPhone) {
      toast.error(
        'Error: ' + 'Phone number not found. Please try again.',
      );
      setShowOtpForm(false);
      setMaskedPhone('');
      return;
    }
    verifyOtpMutation.mutate({ phone: currentPhone, pin: data.pin });
  };

  const handleResendOtp = async () => {
    if (!currentPhone) {
        toast.error(
        'Error: ' + 'Phone number not found. Please try again.',
      );
      setShowOtpForm(false);
      setMaskedPhone('');
      return;
    }
    sendOtpMutation.mutate(currentPhone);
  };

  const handleChangePhone = () => {
    setShowOtpForm(false);
    setMaskedPhone('');
    setCurrentPhone('');
    phoneForm.reset();
    otpForm.reset();
  };

  // No sessionStorage/localStorage for phone number, so no effect needed

  // Get loading state from all mutations
  const isLoading = sendOtpMutation.isPending || verifyOtpMutation.isPending;

  return (
    <div className="w-full max-w-sm mx-auto">
      {!showOtpForm ? (
        // Phone Form
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Car className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome to Timelyyy</h1>
            <p className="text-gray-600 mt-2">Enter your WhatsApp number to continue</p>
          </div>

          <Form {...phoneForm}>
            <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-4">
              <FormField
                control={phoneForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">+91</span>
                        <Input
                          {...field}
                          type="tel"
                          placeholder="Enter your number"
                          className="flex-1"
                          disabled={isLoading}
                          maxLength={10}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Send OTP'}
              </Button>
            </form>
          </Form>
        </div>
      ) : (
        // OTP Form
        <div className="space-y-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Enter OTP</h1>
            <p className="text-gray-600 mt-2">Sent to {maskedPhone}</p>
          </div>

          <Form {...otpForm}>
            <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-4">
            <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">+91</span>
                <Input
                  type="tel"
                  value={maskedPhone}
                  className="flex-1 border-none focus:ring-0"
                  disabled={true}
                />
              </div>
              <FormField
                control={otpForm.control}
                name="pin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Enter 6-digit code</FormLabel>
                    <FormControl>
                      <InputOTP maxLength={6} {...field}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Verifying...' : 'Verify OTP'}
              </Button>

              {resendTimer > 0 ? (
                <p className="text-center text-sm text-gray-500">
                  Resend in {formatTime(resendTimer)}
                </p>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={handleResendOtp}
                  disabled={isLoading}
                >
                  Resend OTP
                </Button>
              )}

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={handleChangePhone}
                disabled={isLoading}
              >
                Change Phone Number
              </Button>
            </form>
          </Form>
        </div>
      )}
    </div>
  );
} 